import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { getAgencyDetail} from "store/agency/thunk";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_BANNER_VISIBLE_MENU,
  LIST_OPTION_ADDITIONAL_SECTION_EXPOSURE,
  LIST_OPTION_BANNER_VISIBLE_MENU,
  LIST_OPTION_PREMIUM_BANNER_PLACEMENT,
} from "helpers/constans";
import { formatDateStringToKorea } from "helpers/format";
import { unique } from "helpers/validate_yup";
import "react-phone-number-input/style.css";
import { NumericFormat } from "react-number-format";
import { postBannerPosition, putBannerPosition } from "store/banner-position/thunk";
import DatePicker from "react-datepicker";
import DropdownOptionMulti from "components/Common/DropdownOptionMulti";

export interface Props {
  id?: string;
  item?: any;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);

const PARAMS_KEY_DEFINE = ["agency", "campaign"];

const BannerPremiumForm = ({
  id = "",
  item = "",
  triggerRefresh,
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {...values};
      const response: any = id
        ? await putBannerPosition(id, data)
        : await postBannerPosition(data);

      if (response?.data || response?.code == 200) {
        setIsLoading((_prev) => false);
        toast(
          `${
            id
              ? t("The banner position has been updated.")
              : t("The banner position has been created.")
          }`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    positionCode: Yup.string().required(`${t("This field is required")}`),
    name: Yup.string().required(`${t("This field is required")}`),
    visibleInMenu: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      startTime: new Date(),
      endTime: new Date(),
      premiumBannerPlacement: [],
      additionalSectionExposure: [],
      status: "ACTIVE"
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    if(item) {
      formik.setValues({
        ...item,
        startTime: item?.startTime ? new Date(formatDateStringToKorea(item?.startTime)) : null,
        endTime: item?.endTime ? new Date(formatDateStringToKorea(item?.endTime)) : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    document.title = `${t("Create Banner Position")} - ${t(
      "Banner Position"
    )} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Banner Premium") : t("Create Banner Premium")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 4) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  <Col md={3} lg={3} className="datepicker-time">
                    <div className="mb-3">
                      <Label htmlFor="startTime" className="form-label">
                        {t("Exposure Start")} <span className="text-danger">*</span>
                      </Label>

                      <div className="w-100">
                        <DatePicker
                          name={"startTime"}
                          className="form-control"
                          selected={formik?.values?.startTime}
                          placeholderText={t("Exposure Start")}
                          onChange={(date: any) => {
                            formik.setFieldValue("startTime", date);
                          }}
                          dateFormat="yyyy-MM-dd"
                          isClearable={true}
                        />
                      </div>

                      {formik.touched.startTime && formik.errors.startTime ? (
                        <div className="text-danger mt-2">
                          {String(formik.errors.startTime)}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col md={3} lg={3} className="datepicker-time">
                    <div className="mb-3">
                      <Label htmlFor="endTime" className="form-label">
                        {t("Exposure End")} <span className="text-danger">*</span>
                      </Label>

                      <div className="w-100">
                        <DatePicker
                          name={"endTime"}
                          className="form-control"
                          selected={formik?.values?.endTime}
                          placeholderText={t("Exposure Start")}
                          onChange={(date: any) => {
                            formik.setFieldValue("endTime", date);
                          }}
                          dateFormat="yyyy-MM-dd"
                          isClearable={true}
                        />
                      </div>

                      {formik.touched.endTime && formik.errors.endTime ? (
                        <div className="text-danger mt-2">
                          {String(formik.errors.endTime)}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}></Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Premium Banner Placement")}{" "}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownOptionMulti
                        name="premiumBannerPlacement"
                        placeholder={`${t("Premium Banner Placement")}...`}
                        initialValue={formik?.values?.premiumBannerPlacement || null}
                        dataList={LIST_OPTION_PREMIUM_BANNER_PLACEMENT}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue(
                            "premiumBannerPlacement",
                            event
                          );
                        }}
                        isHasOptionAll={false}
                      />

                      {formik?.touched?.premiumBannerPlacement &&
                      formik?.errors?.premiumBannerPlacement ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.premiumBannerPlacement}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Additional Section Exposure")}{" "}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownOptionMulti
                        name="additionalSectionExposure"
                        placeholder={`${t("Additional Section Exposure")}...`}
                        initialValue={formik?.values?.additionalSectionExposure || null}
                        dataList={LIST_OPTION_ADDITIONAL_SECTION_EXPOSURE}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue(
                            "additionalSectionExposure",
                            event
                          );
                        }}
                        isHasOptionAll={false}
                      />

                      {formik?.touched?.additionalSectionExposure &&
                      formik?.errors?.additionalSectionExposure ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.additionalSectionExposure}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Status")}
                      </Label>
                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="setting-auto-slide"
                            checked={formik?.values?.status === "ACTIVE" ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue(
                                "status",
                                event?.target?.checked
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status &&
                      formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.status}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Update Banner Premium")
                          : t("Create Banner Premium")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default BannerPremiumForm;
